import { useEffect, useRef } from "react";
import { useMarkWholesaleViewed, useWholesaleDetail } from "../../api/wholesaleQueries.js";
import { AsyncPanel } from "../../ui/AsyncPanel.js";
import { useWholesaleUrlState } from "./useWholesaleUrlState.js";
import { WholesaleList } from "./WholesaleList.js";
import { WholesaleOrderInspector } from "./WholesaleOrderInspector.js";
import { WholesaleShell } from "./WholesaleShell.js";

/**
 * `/wholesale` — the wholesale workspace (spec §4.2–§4.5): the same three
 * panels as the service one — the wholesale sidebar, the list (Open,
 * Arrived · 36h, History, All orders, Search) and the inspector for the
 * selected order — under the shared shell.
 *
 * All visible state is in the URL (`view`, `order`, `q`, `customer`), so
 * a reload or a link restores the exact screen; selecting a row writes
 * `order=<id>` and mounts the inspector. Opening an order acknowledges it
 * as viewed through the read cursor the server returned with the detail,
 * exactly once per `(order, cursor)`.
 */

export function WholesaleWorkspacePage() {
  const url = useWholesaleUrlState();
  return (
    <WholesaleShell url={url}>
      <WholesaleList url={url} />
      <aside className="workspace__inspector" aria-label="Order inspector">
        <InspectorPane orderId={url.state.orderId} />
      </aside>
    </WholesaleShell>
  );
}

function InspectorPane({ orderId }: { orderId: string | null }) {
  const detail = useWholesaleDetail(orderId);
  const markViewed = useMarkWholesaleViewed();
  const acknowledged = useRef<string | null>(null);
  const mutate = markViewed.mutate;

  useEffect(() => {
    const data = detail.data;
    if (!orderId || !data || data.order.id !== orderId) return;
    const cursor = data.readThroughActivityEventId;
    if (!cursor) return;
    const key = `${orderId}:${cursor}`;
    if (acknowledged.current === key) return;
    acknowledged.current = key;
    mutate({ orderId, throughActivityEventId: cursor });
  }, [orderId, detail.data, mutate]);

  if (!orderId) {
    return (
      <AsyncPanel
        isEmpty
        emptyTitle="Select an order"
        emptyDescription="Choose a wholesale order from the list to see its parts, comments and activity here."
        className="inspector-pane__empty"
      >
        {null}
      </AsyncPanel>
    );
  }

  const loaded = detail.data !== undefined;
  return (
    <AsyncPanel
      isLoading={detail.isPending}
      error={loaded ? undefined : detail.error}
      refreshError={loaded ? detail.error : undefined}
      refreshTitle="Couldn't refresh this order."
      onRetry={() => {
        void detail.refetch();
      }}
      loadingLabel="Loading order…"
      className="inspector-pane__state"
    >
      {detail.data ? <WholesaleOrderInspector detail={detail.data} /> : null}
    </AsyncPanel>
  );
}
